import { Modal as BootstrapModal } from 'react-bootstrap';
import styled from 'styled-components';
import Course33VideoShare from '@/models/course-33-share';
import { Button, ButtonToolbar } from '@/shared/components/Button';
import { colorBackground, colorText } from '@/utils/palette';
import { flexFlow } from '@/utils/directions';
import { formatDate } from '@/utils/helpers';
import { darkTextDim } from '@/shared/dark-inline-styles';

interface VideoPreviewModalProps {
    isOpen: boolean;
    share?: Course33VideoShare;
    toggle: () => void;
}

const labelStyle: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: darkTextDim, margin: '0 0 2px' };
const valueStyle: React.CSSProperties = { fontSize: 14, margin: '0 0 10px', whiteSpace: 'pre-line', wordBreak: 'break-word' };

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p style={labelStyle}>{label}</p>
        <p style={valueStyle}>{children}</p>
    </div>
);

/**
 * VIEW-ONLY preview of a Course-33 video share. Streams the share's signed
 * `videoUrl` in a native <video controls> player. There is intentionally NO
 * download, NO delete, and NO edit here — staff review only. `controlsList`,
 * disabled picture-in-picture and a suppressed context menu are UX-level
 * download deterrents; the real protection is the API's time-limited signed URL.
 */
const VideoPreviewModal = ({ isOpen, share, toggle }: VideoPreviewModalProps) => {
    return (
        <StyledModal show={isOpen} onHide={toggle} backdrop="static" keyboard={false} size="lg">
            <ModalHeader>
                <ModalTitle>{share ? `Day ${share.day} — ${share.userId}` : 'Video'}</ModalTitle>
            </ModalHeader>
            <ModalBody>
                {share && (
                    <>
                        {/* `key` forces the <video> to remount per share so it never keeps
                            playing the previous user's clip when a different row is opened. */}
                        <video
                            key={share.id}
                            src={share.videoUrl}
                            controls
                            controlsList="nodownload noplaybackrate"
                            disablePictureInPicture
                            onContextMenu={(e) => e.preventDefault()}
                            style={{ width: '100%', maxHeight: '60vh', borderRadius: 6, background: '#000', display: 'block', marginBottom: 16 }}
                        />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left' }}>
                            <Field label="User">{share.userId}</Field>
                            <Field label="Day">{share.day}</Field>
                            <Field label="Period (Enrollment)">{share.enrollmentId}</Field>
                            <Field label="Uploaded">{formatDate(share.createdAt)}</Field>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <Field label="Caption">{share.caption?.trim() ? share.caption : '—'}</Field>
                            </div>
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button variant="secondary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </StyledModal>
    );
};

export default VideoPreviewModal;

const ModalTitle = styled.h4`
    margin-top: 10px;
    margin-bottom: 15px;
    font-weight: 700;

    &:first-child {
        margin-top: 0;
    }
`;

const ModalHeader = styled.div``;
const ModalBody = styled.div``;

const ModalFooter = styled(ButtonToolbar)`
    margin-top: 20px;
    display: flex;
    justify-content: center;
    margin-bottom: 0;

    button {
        min-width: 100px;
        padding: 4px 25px;
        margin-bottom: 0;
        display: inline-block;
    }

    flex-flow: ${flexFlow} !important;
`;

const StyledModal = styled(BootstrapModal)`
    .modal-content {
        border-radius: 0;
        border: none;
        box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
        position: relative;
        padding: 50px 40px 60px 40px;
        text-align: center;
        background-color: ${colorBackground};
        color: ${colorText};
    }
`;
